import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Scale,
  AlertCircle,
  Upload,
  CheckCircle2,
  Loader2,
  Home,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * iter186: Página pública para que un matriculado de otro distrito corrija
 * una solicitud OBSERVADA usando el token único recibido por email.
 *
 * Flujo:
 * 1. GET /publico/correccion/{token} → devuelve la solicitud + motivo
 * 2. Usuario re-sube los adjuntos (opcional: si no sube, se reutilizan los originales)
 * 3. POST /publico/correccion/{token}/enviar → actualiza y marca pendiente_nueva_revision
 */
export default function OtrosDistritosCorreccionPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [errorInit, setErrorInit] = useState('');
  const [sol, setSol] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [ok, setOk] = useState(false);

  const [firmadosByDepto, setFirmadosByDepto] = useState({});
  const [certificado, setCertificado] = useState(null);
  const [copiaDni, setCopiaDni] = useState(null);
  const [sinCertificado, setSinCertificado] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/auxiliares-justicia/publico/correccion/${token}`,
        );
        setSol(res.data);
        setSinCertificado(Boolean(res.data?.sin_certificado_aun));
        // iter194: si el admin pidió "inicio_formulario" (corrección completa),
        // redirigir al flujo de formulario público en modo corrección.
        if ((res.data?.punto_reinicio_observacion || '').trim() === 'inicio_formulario') {
          try {
            sessionStorage.setItem('aux_otro_distrito_origen', res.data.distrito_origen || '');
            localStorage.setItem(
              'aux_otro_distrito_formdata',
              JSON.stringify({
                formData: res.data.datos || {},
                cursoEsteAnio: Boolean(res.data?.datos?.curso_este_anio),
              }),
            );
            localStorage.setItem(
              'aux_otro_distrito_correccion',
              JSON.stringify({
                token,
                motivo: res.data.motivo_observacion || '',
                departamentos_judiciales: res.data.departamentos_judiciales || [],
              }),
            );
          } catch { /* no-op */ }
          navigate(
            `/auxiliares-justicia/otros-distritos/formulario?corregir=${encodeURIComponent(token)}`,
            { replace: true },
          );
          return;
        }
      } catch (err) {
        setErrorInit(err.response?.data?.detail || 'Enlace inválido o expirado.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token, navigate]);

  const enviar = async () => {
    setErrorMsg('');
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append('datos_json', JSON.stringify(sol.datos || {}));
      const deptos = sol.departamentos_judiciales || [];
      deptos.forEach((d) => {
        if (firmadosByDepto[d]) {
          fd.append('formularios_firmados', firmadosByDepto[d], firmadosByDepto[d].name);
        }
      });
      // Solo enviamos deptos para los firmados que sí subimos (si no, backend reutiliza)
      const deptosConArchivo = deptos.filter((d) => firmadosByDepto[d]);
      fd.append('formularios_firmados_deptos', JSON.stringify(deptosConArchivo));
      if (certificado) fd.append('certificado', certificado, certificado.name);
      if (copiaDni) fd.append('copia_dni', copiaDni, copiaDni.name);
      fd.append('sin_certificado_aun', String(sinCertificado));

      await axios.post(
        `${API_URL}/auxiliares-justicia/publico/correccion/${token}/enviar`,
        fd,
      );
      setOk(true);
    } catch (err) {
      const d = err.response?.data?.detail || 'Error al enviar la corrección.';
      setErrorMsg(typeof d === 'string' ? d : JSON.stringify(d));
    } finally {
      setEnviando(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (errorInit) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full border-red-200" data-testid="correccion-error-token">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-14 h-14 text-red-500 mx-auto mb-3" />
            <h2 className="text-lg font-bold mb-2">Enlace no válido</h2>
            <p className="text-sm text-gray-600 mb-4">{errorInit}</p>
            <Link to="/">
              <Button variant="outline">
                <Home className="w-4 h-4 mr-2" /> Volver al inicio
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (ok) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full border-green-200" data-testid="correccion-ok">
          <CardContent className="pt-6 text-center">
            <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-3" />
            <h2 className="text-lg font-bold mb-2">Corrección enviada</h2>
            <p className="text-sm text-gray-600 mb-4">
              La administración recibió tu corrección y te contactará con la resolución.
            </p>
            <Link to="/">
              <Button variant="outline">
                <Home className="w-4 h-4 mr-2" /> Volver al inicio
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const deptos = sol?.departamentos_judiciales || [];

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-center mb-6">
          <img src="/logo-colegio.png" alt="Logo del Colegio" className="h-20 w-auto object-contain" />
        </div>
        <Card data-testid="correccion-page">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-amber-600" />
              <CardTitle className="text-base">Corrección de solicitud · Otro distrito</CardTitle>
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Distrito de origen: <strong>{sol?.distrito_origen}</strong> · Solicitante:{' '}
              <strong>
                {sol?.datos?.apellido}, {sol?.datos?.nombres}
              </strong>
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            {sol?.motivo_observacion && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
                <p className="font-semibold mb-1">Motivo de la observación</p>
                <p className="whitespace-pre-wrap">{sol.motivo_observacion}</p>
              </div>
            )}

            <p className="text-xs text-gray-600">
              Volvé a subir los adjuntos corregidos. Si no cambiás alguno, se mantiene el original.
            </p>

            {deptos.map((dep) => (
              <div key={dep} className="border rounded-lg p-3">
                <Label className="text-sm font-medium">
                  Formulario firmado — {dep} <span className="text-xs text-gray-400">(opcional)</span>
                </Label>
                <Input
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={(e) =>
                    setFirmadosByDepto((prev) => ({
                      ...prev,
                      [dep]: e.target.files?.[0] || null,
                    }))
                  }
                  className="mt-2"
                  data-testid={`correccion-input-firmado-${dep.replace(/\s+/g, '-')}`}
                />
                {firmadosByDepto[dep] && (
                  <p className="text-xs text-green-700 mt-1">✓ {firmadosByDepto[dep].name}</p>
                )}
              </div>
            ))}

            <div className="border rounded-lg p-3 bg-amber-50">
              <Label className="text-sm font-medium">
                Copia DNI <span className="text-xs text-gray-500">(opcional, se mantiene el original si no la subís)</span>
              </Label>
              <Input
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => setCopiaDni(e.target.files?.[0] || null)}
                className="mt-2"
                data-testid="correccion-input-copia-dni"
              />
              {copiaDni && <p className="text-xs text-green-700 mt-1">✓ {copiaDni.name}</p>}
            </div>

            <div className="border rounded-lg p-3">
              <Label className="text-sm font-medium">
                Certificado / Acta del curso{' '}
                <span className="text-xs text-gray-400">(opcional)</span>
              </Label>
              <Input
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => setCertificado(e.target.files?.[0] || null)}
                disabled={sinCertificado}
                className="mt-2"
                data-testid="correccion-input-certificado"
              />
              {certificado && !sinCertificado && (
                <p className="text-xs text-green-700 mt-1">✓ {certificado.name}</p>
              )}
              <div className="flex items-center gap-2 mt-2">
                <Checkbox
                  id="sin-cert-corr"
                  checked={sinCertificado}
                  onCheckedChange={(v) => {
                    setSinCertificado(Boolean(v));
                    if (v) setCertificado(null);
                  }}
                  data-testid="correccion-chk-sin-certificado"
                />
                <Label htmlFor="sin-cert-corr" className="text-xs text-gray-600 cursor-pointer">
                  Aún no tengo el certificado
                </Label>
              </div>
            </div>

            {errorMsg && (
              <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{errorMsg}</span>
              </div>
            )}

            <Button
              className="w-full bg-amber-600 hover:bg-amber-700"
              onClick={enviar}
              disabled={enviando}
              data-testid="correccion-btn-enviar"
            >
              {enviando ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando…
                </>
              ) : (
                <>
                  Enviar corrección
                  <Upload className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
